/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import {
	FREE_EDITION,
	STANDARD_EDITION,
	UNLICENSED,
} from '@jira/platform__application-editions/src/edition.tsx';
import {
	SERVICE_DESK,
	SOFTWARE,
	CUSTOMER_SERVICE,
} from '@jira/platform__application-editions/src/application-key.tsx';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { useApiKeyManagementInfo } from '@jira/eoc__api-key-management/src/controllers/index.tsx';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import { withThrowOnError } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/throw-when-no-data-and-error/index.tsx';
import { useNavigationGlobalResource } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/controllers/navigation-global-resource/index.tsx';
import { hasItemInSidebar } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/utils/has-item-in-sidebar/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { RouteResourceError } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
// eslint-disable-next-line jira/restricted/fedramp-isolated-cloud
import { isFedRamp, isIsolatedCloud } from '@atlassian/atl-context';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { Features } from '../../common/types.tsx';

type UseFeaturesReturn = {
	data: Features | null;
	loading: boolean;
	error: RouteResourceError | null;
};

type UseFeaturesArgs = {
	hasJsmAdminAccess?: boolean;
};

const isServiceDeskAboveStandard = (serviceDeskEdition?: string) =>
	serviceDeskEdition !== undefined &&
	serviceDeskEdition !== FREE_EDITION &&
	serviceDeskEdition !== STANDARD_EDITION;

const useFeaturesInternal = ({
	hasJsmAdminAccess = false,
}: UseFeaturesArgs = {}): UseFeaturesReturn => {
	const {
		data: navigationGlobalResourceData,
		loading: navigationGlobalResourceLoading,
		error: navigationGlobalResourceError,
	} = useNavigationGlobalResource();
	const {
		shouldUseJSM,
		shouldAccessUnlockOperations,
		isTransitioning,
		isTransitioned,
		loading: ogTenantInfoLoading,
		error: ogTenantInfoError,
	} = useOgTenantInfo();

	const tenantContext = useTenantContext();

	const appEditions = useAppEditions();

	const { hasKeys } = useApiKeyManagementInfo();

	// There are two considerations here to prevent flickering in the nav sidebar:
	// 1. When the EOC_TENANT resource (used by `useOgTenantInfo`) fails to fetch from the server,
	// a refetch is triggered *once* from the client. This means that if there is a server-side
	// ogTenantInfoError, we should wait for the client-side refetch to complete before returning
	// a non-null value for `data`.
	// 2. The EOC_CHECK_API_KEY resource (used by `useApiKeyManagementInfo`) is a browser-only
	// resource that is dependent on the EOC_TENANT resource. This means that we shouldn't wait
	// for it to load before rendering the nav sidebar.
	const ogTenantInfoServerError = __SERVER__ ? ogTenantInfoError : null;
	if (
		navigationGlobalResourceLoading ||
		!navigationGlobalResourceData ||
		ogTenantInfoLoading ||
		ogTenantInfoServerError
	) {
		return {
			data: null,
			loading: navigationGlobalResourceLoading || ogTenantInfoLoading,
			error: navigationGlobalResourceError || ogTenantInfoServerError,
		};
	}
	const hasJsmAboveStandard =
		Boolean(tenantContext.licensedProducts[SERVICE_DESK]) &&
		isServiceDeskAboveStandard(appEditions.serviceDesk);
	const hasJsmAppAdminOnlyAccess =
		!tenantContext.isAdmin && // User is not a Jira admin
		hasJsmAdminAccess; // User has JSM admin permission
	const hasJsmSettingsAdminAccess = tenantContext.isAdmin || hasJsmAppAdminOnlyAccess;
	const hasShiftFromOpsgenieToJira = shouldAccessUnlockOperations;
	const hideFeaturesIsolatedCloud =
		isIsolatedCloud() && !fg('fix_chatops_access_in_isolated_cloud');
	const hasServiceDeskLicense = Boolean(tenantContext.licensedProducts[SERVICE_DESK]);

	const hasChatOpsEnvironmentAccess = fg('aem_nav_global_products')
		? isFeatureEnabled('jira-service-management', 'jsm-chat-ops') &&
			(!isFeatureEnabled('jira-service-management', 'jsm-chat-ops-ic-custom-apps') ||
				fg('fix_chatops_access_in_isolated_cloud'))
		: !isFedRamp() && !hideFeaturesIsolatedCloud;
	const hasChatOpsToolAccess =
		!shouldUseJSM && hasServiceDeskLicense && hasChatOpsEnvironmentAccess;

	return {
		data: {
			hasApiKeyManagementAccess:
				(isTransitioning ||
					(isTransitioned && fg('api_key_management_open_for_transitioned_users'))) &&
				shouldUseJSM &&
				hasJsmAboveStandard &&
				tenantContext.isSiteAdmin &&
				hasKeys,
			hasApplicationAccess: hasItemInSidebar(
				navigationGlobalResourceData,
				'application_access_browser',
			),
			hasApplicationLinksIntegration: hasItemInSidebar(
				navigationGlobalResourceData,
				'configure-application-links',
			),
			hasAuditLogsAccess: shouldUseJSM && !!tenantContext.licensedProducts[SERVICE_DESK],
			hasChatOpsToolAccess,
			hasCompassPageIntegration:
				tenantContext.isSiteAdmin && !!tenantContext.licensedProducts[SOFTWARE],
			hasConfluencePageIntegration: tenantContext.isAdmin,
			hasCustomerServiceSection:
				tenantContext.isAdmin && !!tenantContext.licensedProducts[CUSTOMER_SERVICE],
			hasDvcsAccountsIntegration: hasItemInSidebar(
				navigationGlobalResourceData,
				'bitbucket_bulk_repo',
			),
			hasGlobalIncomingCallAccess: shouldUseJSM && hasJsmAboveStandard,
			hasGlobalIntegrationAccess: shouldUseJSM && hasJsmAboveStandard,
			hasGlobalSyncAccess: shouldUseJSM && hasJsmAboveStandard,
			hasIntegrationsSection: hasItemInSidebar(
				navigationGlobalResourceData,
				'admin_applications_integrations_section',
			),
			hasMaintenanceAccess: shouldUseJSM && !!tenantContext.licensedProducts[SERVICE_DESK],
			hasShiftFromOpsgenieToJira,
			hasOperationsSection:
				tenantContext.isAdmin &&
				Boolean(tenantContext.licensedProducts[SERVICE_DESK]) &&
				(hasShiftFromOpsgenieToJira || hasChatOpsToolAccess),
			hasOrganizationsListAccess:
				!!tenantContext.licensedProducts[SERVICE_DESK] && hasJsmSettingsAdminAccess,
			hasPlansSection: hasItemInSidebar(navigationGlobalResourceData, 'jpo-ws-main-admin'),
			hasPoliciesAccess: shouldUseJSM && hasJsmAboveStandard,
			hasProductDiscoverySection:
				tenantContext.isAdmin && appEditions.productDiscovery !== UNLICENSED,
			hasRoleBasedNotificationsAccess: shouldUseJSM && tenantContext.isAdmin && hasJsmAboveStandard,
			hasServiceManagementAuthenticationSettings:
				hasJsmSettingsAdminAccess && appEditions.serviceDesk !== UNLICENSED,
			hasServiceManagementConfig: hasItemInSidebar(
				navigationGlobalResourceData,
				'sd-configuration-link',
			),
			hasServiceManagementCustomerAccess:
				hasJsmSettingsAdminAccess && appEditions.serviceDesk !== UNLICENSED,
			hasServiceManagementEmailRequests: hasItemInSidebar(
				navigationGlobalResourceData,
				'sd-admin-mail-info-link',
			),
			hasServiceManagementIncidentManagementTools: hasItemInSidebar(
				navigationGlobalResourceData,
				'sd-incident-management-tools-link',
			),
			hasServiceManagementKbPermissionsAccess: hasJsmSettingsAdminAccess,
			hasServiceManagementSection:
				hasJsmSettingsAdminAccess &&
				hasItemInSidebar(navigationGlobalResourceData, 'sd-plugin-admin-section'),
			hasServiceManagementWorkforceSkills:
				hasJsmSettingsAdminAccess && hasJsmAboveStandard && fg('jsm_wfo_skills'),
			hasSoftwareConfig:
				tenantContext.isAdmin &&
				(!expVal('sea-3997-hide-jira-global-settings-when-jsm-only', 'isEnabled', false) ||
					!!tenantContext.licensedProducts[SOFTWARE]),
			hasSoftwareSection:
				tenantContext.isAdmin &&
				(!expVal('sea-3997-hide-jira-global-settings-when-jsm-only', 'isEnabled', false) ||
					!!tenantContext.licensedProducts[SOFTWARE]),
			hasStatusPageIntegration:
				!!tenantContext.atlassianAccountId &&
				hasItemInSidebar(navigationGlobalResourceData, 'jsd-status-page__admin-page'),
			hasJsmAppAdminOnlyAccess,
		},
		loading: navigationGlobalResourceLoading,
		error: navigationGlobalResourceError,
	};
};

export const useFeatures: (args?: UseFeaturesArgs) => UseFeaturesReturn =
	withThrowOnError(useFeaturesInternal);
